import { RefCountedAsyncResourceQueue, type ResourceSnapshot } from "../_internal/RefCountedAsyncResourceQueue";
import { type HeicConversionQueue, heicConversionQueue } from "../useHeicConversionQueue";
import { MAX_THUMBNAIL_RETRIES } from "./constants";
import type { ImageAcquireOptions, ImageCacheOptions, ImageCacheSnapshot, ImageCacheSubscriber } from "./types";

const DEFAULT_DECODE_CONCURRENCY = 4;
/**
 * 기본 30 entries.
 *
 * 각 entry 는 디코드된 비트맵을 보유하므로 곧 메모리 상한이다.
 * 모바일 PWA 의 WebView 메모리 한계와 2-3열 그리드 viewport 크기를 고려한 보수적 값.
 */
const DEFAULT_MAX_ENTRIES = 30;
/**
 * 한 entry 의 생애 동안 허용하는 최대 resolve(=변환+디코드) 시도 횟수.
 *
 * 재 acquire 마다 자동 재시도하지만, 깨진 파일 / 영구적인 변환 실패에 대해 끝없이 재시도하는 것을 막는다.
 * cap 도달 시 status='error' 가 잠긴다. LRU evict 후 다음 acquire 는 fresh entry 라 카운터가 0 부터 다시.
 *
 * 하위 `HeicConversionQueue` 에도 동일 정책의 자체 cap 이 있어, 변환 실패가 영구화되면
 * 이 layer 의 retry 도 자연스럽게 (queue 가 즉시 error 를 돌려주므로) 빠르게 cap 에 도달한다.
 *
 * `useImageWithThumbnail` 의 `MAX_THUMBNAIL_RETRIES` 보다 커야 컴포넌트의 각 retry 가
 * 실제 네트워크 재시도로 이어진다. +2 는 안전 여유 — 변환 실패 후 즉시 디코드를 한 번 더 허용.
 */
const MAX_RESOLVE_ATTEMPTS = MAX_THUMBNAIL_RETRIES + 2;

const HEIC_EXTENSION_PATTERN = /\.(heic|heif)$/i;

/**
 * HEIC 로 다뤄야 하는 URL 의 내부 캐시 키 prefix.
 *
 * blob URL 처럼 확장자가 없어 sniff 로 HEIC 임을 알 수 없는 소스는, 호출자가 넘긴
 * `isHeic` 플래그를 이 prefix 로 키에 인코딩해 변환 경로로 보낸다. NUL 문자로 구분해
 * 실제 URL 과 절대 충돌하지 않으며, DOM 에는 노출되지 않는다 (내부 키 전용).
 */
const HEIC_KEY_PREFIX = "\0heic\0";

/** 확장자 기반 HEIC 추정. `isHeic` 가 명시되지 않은 소스의 기본 판정에 쓰인다. */
function defaultIsHeic(src: string): boolean {
  const pathname = src.split("?")[0] ?? "";
  return HEIC_EXTENSION_PATTERN.test(pathname);
}

/**
 * 디코드된 비트맵을 hot 하게 잡고 있는 off-DOM `<img>` + 그 src 였던 표시용 URL.
 *
 * `warmImage` 는 React `<img>` 와 별개의 인스턴스이지만 동일 URL 을 들고 있는 한
 * 브라우저는 디코드된 비트맵을 메모리에 유지한다 (Chrome/Safari 모두 확인).
 * 이 reference 가 사라져야 GC 대상이 된다.
 */
interface ImageResolveResult {
  url: string;
  warmImage: HTMLImageElement;
}

/**
 * 모든 표시 이미지의 단일 캐시.
 *
 * 책임
 * - 입력 src 를 표시 가능한 URL 로 결정한다 (HEIC 는 {@link HeicConversionQueue} 위임).
 * - 결정된 URL 의 비트맵을 off-DOM `<img>` 로 사전 디코드해 메모리에 핀한다.
 *   덕분에 React `<img>` 가 unmount/mount 를 반복해도 (가상화 환경) 같은 비트맵이
 *   브라우저 캐시에 살아있어 다시 디코드해도 사실상 즉시 표시되며 흰 깜박임이 사라진다.
 * - 디코드 비트맵 수를 LRU 로 제한해 PWA 의 메모리 폭주를 방지한다.
 *
 * 비책임
 * - DOM 에 붙은 React `<img>` 의 가시성 전환 (페이드인) 은 호출자 컴포넌트 책임.
 *   캐시는 "준비 완료" 신호만 발행하고, 실제 표시 타이밍은 UI 가 결정한다.
 *
 * 구현
 * - 인프라 (LRU / refCount / pauseOnHidden / 동시성 / 재시도 캡) 는
 *   {@link RefCountedAsyncResourceQueue} 가 담당한다.
 * - resolve(HEIC) → decode 두 단계는 단일 `run()` 안에서 합성되며, 외부에는
 *   단일 `loading` 상태로만 노출된다 (base 의 idle/running → loading).
 */
export class ImageCache {
  private readonly _queue: RefCountedAsyncResourceQueue<ImageResolveResult>;

  /**
   * HEIC entry 가 살아있는 동안 heic queue 의 ref 를 유지하기 위한 release 함수.
   *
   * 이 ref 를 놓으면 heicQueue 가 해당 entry 를 LRU evict 할 수 있고, 그 결과
   * `URL.revokeObjectURL` 이 호출되어 우리 warmImage 의 src 가 깨진다.
   * 따라서 entry 가 evict / destroy 될 때까지 보존한다.
   */
  private readonly _heicReleases = new Map<string, () => void>();

  private readonly _isHeic: (src: string) => boolean;
  private readonly _heicQueue: HeicConversionQueue;
  private readonly _createImageElement: () => HTMLImageElement;

  constructor(options?: ImageCacheOptions) {
    this._isHeic = options?.isHeic ?? defaultIsHeic;
    this._heicQueue = options?.heicQueue ?? heicConversionQueue;
    this._createImageElement = options?.createImageElement ?? (() => new Image());

    this._queue = new RefCountedAsyncResourceQueue<ImageResolveResult>({
      run: (key) => this._resolveAndDecode(key),
      onCleanup: (key, result) => this._cleanupEntry(key, result),
      concurrency: options?.decodeConcurrency ?? DEFAULT_DECODE_CONCURRENCY,
      maxCacheSize: options?.maxEntries ?? DEFAULT_MAX_ENTRIES,
      maxAttempts: MAX_RESOLVE_ATTEMPTS,
      getCacheKey: options?.getCacheKey,
      pauseOnHidden: options?.pauseOnHidden,
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * 부수효과 없이 현재 캐시 상태를 조회한다.
   *
   * - LRU 순서를 갱신하지 않고, refCount 도 변경하지 않는다.
   * - 컴포넌트 `useState` 초기값에서 캐시 hit 을 체크해 첫 commit 부터 ready 상태를
   *   노출하는 데 사용한다 (skeleton/흰 깜박임 1프레임 제거).
   */
  peek(src: string, options?: ImageAcquireOptions): ImageCacheSnapshot | null {
    const snap = this._queue.peek(this._deriveKey(src, options));
    return snap ? this._toImageSnapshot(snap) : null;
  }

  /**
   * 캐시 결과를 구독한다.
   *
   * - 캐시에 결과가 있으면 즉시 현재 스냅샷으로 콜백.
   * - 없으면 entry 를 만들어 resolve(HEIC 변환) → decode 사전 가열 파이프라인을 시작한다.
   * - 호출 즉시 refCount 가 1 증가하므로, 반환된 release 함수를 반드시 호출해야 한다.
   *
   * @returns 구독 해제 함수. 호출 시 refCount 1 감소 + 필요 시 evict 시도. 멱등.
   */
  acquire(src: string, subscriber: ImageCacheSubscriber, options?: ImageAcquireOptions): () => void {
    return this._queue.acquire(this._deriveKey(src, options), (snap) => subscriber(this._toImageSnapshot(snap)));
  }

  /**
   * 큐 전체를 즉시 정리한다.
   *
   * 진행 중인 디코드는 cancel 불가하므로 결과는 도착 즉시 폐기된다.
   * 모든 HEIC ref 가 release 되며, 보유한 warmImage reference 도 끊어 GC 가능 상태로 만든다.
   */
  destroy(): void {
    this._queue.destroy();
    // base 가 entry 별 onCleanup 을 호출했으므로 이 시점이면 _heicReleases 도 모두 비어 있어야 정상.
    // 방어적으로 한 번 더 비운다.
    this._heicReleases.clear();
  }

  /** 테스트/디버깅용 내부 상태 스냅샷 */
  getStats(): {
    cacheSize: number;
    activeDecodeCount: number;
    pendingDecodeCount: number;
    isHidden: boolean;
  } {
    const stats = this._queue.getStats();
    return {
      cacheSize: stats.cacheSize,
      activeDecodeCount: stats.activeCount,
      pendingDecodeCount: stats.pendingCount,
      isHidden: stats.isHidden,
    };
  }

  /* ------------------------------------------------------------------ */
  /*  Private                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * resolve(HEIC 변환) + decode(off-DOM 사전 가열) 를 합성으로 수행하는 base queue 의 run 함수.
   *
   * 진행 중에는 base 의 `running` (→ 외부 `loading`) 상태로만 노출된다.
   */
  private async _resolveAndDecode(key: string): Promise<ImageResolveResult> {
    let resolvedUrl: string;

    if (this._keyIsHeic(key)) {
      try {
        resolvedUrl = await this._awaitHeicConversion(key);
      } catch (err) {
        // heic 실패 → ref 보유 의미 없음. 즉시 release.
        this._heicReleases.get(key)?.();
        this._heicReleases.delete(key);
        throw err;
      }
    } else {
      resolvedUrl = key;
    }

    const img = this._createImageElement();
    img.decoding = "async";
    img.src = resolvedUrl;

    // decode() 미지원 환경 (jsdom 등) 에서는 즉시 ready 로 진행한다.
    if (typeof img.decode === "function") {
      await img.decode();
    }

    return { url: resolvedUrl, warmImage: img };
  }

  /**
   * heicQueue 에 acquire 하여 success/error 까지 기다린다.
   *
   * acquire 의 release 는 entry 가 onCleanup 될 때까지 `_heicReleases` 에 보존된다
   * (heicQueue 가 우리 URL 을 LRU evict 해 revoke 해버리는 것을 방지).
   */
  private _awaitHeicConversion(key: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      let settled = false;
      const sourceUrl = this._stripKey(key);
      const release = this._heicQueue.acquire(sourceUrl, (snap) => {
        if (settled) return;
        if (snap.status === "success" && snap.url) {
          settled = true;
          resolve(snap.url);
        } else if (snap.status === "error") {
          settled = true;
          reject(snap.error ?? new Error("HEIC conversion failed"));
        }
        // idle / converting: 계속 구독 유지
      });
      // sync 콜백으로 이미 settled 됐어도 release 는 entry 생애 동안 보존한다 (성공 케이스).
      // 실패 케이스에선 위 catch 블록에서 release 를 호출한다.
      this._heicReleases.set(key, release);
    });
  }

  /**
   * base entry 가 evict / destroy / 도중-evict 될 때 호출된다.
   *
   * - HEIC ref release (heic 결과 URL 이 우리 warmImage src 인 동안 revoke 되지 않도록 핀해 둔 것)
   * - warmImage src 비우기 (브라우저가 비트맵 메모리를 회수하도록)
   */
  private _cleanupEntry(key: string, result: ImageResolveResult | undefined): void {
    this._heicReleases.get(key)?.();
    this._heicReleases.delete(key);

    if (result) {
      result.warmImage.src = "";
    }
  }

  /**
   * base 의 generic snapshot 을 외부 API 인 `ImageCacheSnapshot` 으로 변환.
   *
   * - success → ready (url=result.url)
   * - error → error
   * - 그 외 (base 의 idle / running) → loading (url 없음)
   */
  private _toImageSnapshot(snap: ResourceSnapshot<ImageResolveResult>): ImageCacheSnapshot {
    const src = this._stripKey(snap.src);
    if (snap.status === "success") {
      return { src, status: "ready", url: snap.result?.url, error: snap.error };
    }
    if (snap.status === "error") {
      return { src, status: "error", url: undefined, error: snap.error };
    }
    return { src, status: "loading", url: undefined, error: snap.error };
  }

  /* ------------------------------------------------------------------ */
  /*  HEIC 내부 키 인코딩                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * 표시용 src 를 내부 캐시 키로 변환한다.
   *
   * HEIC 로 다뤄야 하면 (명시 `isHeic` 또는 확장자 sniff) {@link HEIC_KEY_PREFIX} 를
   * 붙여 변환 경로임을 키에 인코딩한다. 비-HEIC 은 src 가 그대로 키가 된다.
   */
  private _deriveKey(src: string, options?: ImageAcquireOptions): string {
    const treatAsHeic = options?.isHeic ?? this._isHeic(src);
    return treatAsHeic ? HEIC_KEY_PREFIX + src : src;
  }

  private _keyIsHeic(key: string): boolean {
    return key.startsWith(HEIC_KEY_PREFIX);
  }

  private _stripKey(key: string): string {
    return key.startsWith(HEIC_KEY_PREFIX) ? key.slice(HEIC_KEY_PREFIX.length) : key;
  }
}

/**
 * 모듈 싱글톤.
 *
 * 그리드 셀과 라이트박스, 다른 페이지가 동일한 디코드 비트맵 풀을 공유하므로
 * 같은 이미지를 라이트박스로 띄울 때 즉시 표시된다.
 */
export const imageCache = new ImageCache();

export function createImageCache(options?: ImageCacheOptions): ImageCache {
  return new ImageCache(options);
}
